import apiClient from './api';
import { fetchAllPages, normalizeListResponse } from '../utils/paginationUtils';

const employeeService = {
  // Get employees. With `page` set, returns one DRF page. Otherwise walks pages
  // (50/page) so callers still get a full roster without a single huge request.
  getAllEmployees: async (params = {}) => {
    const { skipCache, forceRefresh, ...queryParams } = params;
    const extraConfig = {};
    if (skipCache) extraConfig.skipCache = true;
    if (forceRefresh) extraConfig.forceRefresh = true;
    if (queryParams.page != null) {
      const response = await apiClient.get('/employees/', {
        params: { page_size: 50, ...queryParams },
        ...extraConfig,
      });
      return response.data;
    }
    return fetchAllPages(
      async (pageParams) => {
        const response = await apiClient.get('/employees/', { params: pageParams, ...extraConfig });
        return response.data;
      },
      queryParams,
      { pageSize: queryParams.page_size || 50 }
    );
  },

  // Alias kept for callers that want an explicit “full org” name.
  fetchAllEmployees: async (params = {}) => {
    const { page, ...rest } = params;
    return employeeService.getAllEmployees(rest);
  },

  // Get employee by ID
  getEmployeeById: async (id) => {
    const response = await apiClient.get(`/employees/${id}/`);
    return response.data;
  },

  // Get employee by username
  getEmployeeByUsername: async (username) => {
    const response = await apiClient.get(`/employees/by-username/${username}/`);
    return response.data;
  },

  // Create employee
  createEmployee: async (employeeData) => {
    const response = await apiClient.post('/employees/', employeeData);
    return response.data;
  },

  // Full update employee (PUT)
  updateEmployee: async (id, employeeData) => {
    const response = await apiClient.put(`/employees/${id}/`, employeeData);
    return response.data;
  },

  // Partial update employee (PATCH)
  patchEmployee: async (id, employeeData) => {
    const response = await apiClient.patch(`/employees/${id}/`, employeeData);
    return response.data;
  },

  // Delete employee
  deleteEmployee: async (id) => {
    const response = await apiClient.delete(`/employees/${id}/`);
    return response.data;
  },

  // Admin: Logout employee from all devices
  logoutFromAllDevices: async (id) => {
    const response = await apiClient.post(`/employees/${id}/logout-all/`);
    return response.data;
  },

  // Admin: Set employee password
  setEmployeePassword: async (id, newPassword) => {
    const response = await apiClient.post(`/employees/${id}/set-password/`, {
      new_password: newPassword,
    });
    return response.data;
  },

  // Get team members (for My Team: scope=my_team by default)
  // Pass date (YYYY-MM-DD) to filter timesheet data by that date.
  // Walks paginated pages so callers still receive a full roster array.
  getTeamMembers: async (status = 'all', scope = 'my_team', team = null, date = null, options = {}) => {
    const params = { status, scope, ...options };
    if (team) params.team = team;
    if (date) params.date = date;
    if (options.page != null) return employeeService.getTeamMembersPage(params);
    return fetchAllPages(
      async (pageParams) => {
        const response = await apiClient.get('/employees/team-members/', { params: pageParams });
        return response.data;
      },
      params,
      { pageSize: 50 }
    );
  },

  // Single page of team members (when UI wants load-more instead of full walk).
  getTeamMembersPage: async (params = {}) => {
    const response = await apiClient.get('/employees/team-members/', {
      params: { page_size: 50, ...params },
    });
    return response.data;
  },

  normalizeList: normalizeListResponse,

  // Get employee stats (?scope=my_team for current user's team only)
  getEmployeeStats: async (params = {}) => {
    const response = await apiClient.get('/employees/stats/', { params });
    return response.data;
  },

  // Get per-employee track settings (merged with org defaults)
  getEmployeeTrackSettings: async (employeeId) => {
    const response = await apiClient.get(`/employees/${employeeId}/track-settings/`);
    return response.data;
  },

  // Update per-employee track settings
  updateEmployeeTrackSettings: async (employeeId, settings) => {
    const response = await apiClient.put(`/employees/${employeeId}/track-settings/`, settings);
    return response.data;
  },

  // Generate an invite link for a team (expires in 7 days). Optionally send via email.
  generateInviteLink: async (teamId, email) => {
    const payload = { team_id: teamId };
    if (email) {
      payload.email = email;
      payload.frontend_url = window.location.origin;
    }
    const response = await apiClient.post('/employees/generate-invite-link/', payload);
    return response.data;
  },

  // Resend invite link to an employee (Settings → Manage Employees). Sends email via backend SMTP.
  resendInviteLink: async (employeeId) => {
    const response = await apiClient.post(`/employees/${employeeId}/resend-invite/`, {
      frontend_url: window.location.origin,
    });
    return response.data;
  },

  // Revoke any unaccepted invite links for an employee. The sent email remains, but old links stop working.
  revokeInviteLink: async (employeeId) => {
    const response = await apiClient.post(`/employees/${employeeId}/revoke-invite/`);
    return response.data;
  },

  // Validate invite token (create-password flow). No auth.
  validateInviteToken: async (token) => {
    const response = await apiClient.get('/auth/validate-invite/', { params: { token } });
    return response.data;
  },

  // Accept invite: set password and mark invite used. No auth.
  acceptInvite: async (token, password, confirmPassword) => {
    const response = await apiClient.post('/auth/accept-invite/', {
      token,
      password,
      confirm_password: confirmPassword,
    });
    return response.data;
  },

  // Workstation device management
  getDevices: async (employeeId) => {
    const response = await apiClient.get(`/employees/${employeeId}/devices/`);
    return response.data;
  },

  resetDevices: async (employeeId) => {
    const response = await apiClient.post(`/employees/${employeeId}/reset_devices/`);
    return response.data;
  },
};

export default employeeService;
